import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";
import { locales, useI18n } from "../lib/i18n";
import type { LocaleCode } from "../lib/i18n";

/**
 * The language switch.
 *
 * Deliberately in the top bar rather than buried in settings: a family that
 * needs Urdu needs it on the first screen, before they have learned where
 * anything is.
 */
export function LanguagePicker({ tourId }: { tourId?: string } = {}) {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  // Close on an outside click. Without this the menu survives navigation to
  // another page and hangs over it.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = locales.find((entry) => entry.code === locale) ?? locales[0];

  return (
    <div className="popover-wrap" ref={wrap} data-tour={tourId}>
      <button
        className="role-button language-button"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("top.language")}
      >
        <span className="role-dot">
          <Icon name="message" size={15} />
        </span>
        <span>
          <small>{t("top.language")}</small>
          <strong>{current.native}</strong>
        </span>
        <Icon name="chevron" size={14} />
      </button>

      {open && (
        <div className="popover role-menu glass" role="menu">
          {locales.map((entry) => (
            <button
              key={entry.code}
              role="menuitemradio"
              aria-checked={entry.code === locale}
              className={entry.code === locale ? "selected" : ""}
              onClick={() => {
                setLocale(entry.code as LocaleCode);
                setOpen(false);
              }}
            >
              <span className="role-dot">{entry.code.toUpperCase()}</span>
              <div>
                {/* The native name leads: someone looking for Urdu is looking
                    for "اردو", not for the word "Urdu". */}
                <strong lang={entry.code} dir={entry.dir}>
                  {entry.native}
                </strong>
                <small>{entry.label}</small>
              </div>
              {entry.code === locale && <Icon name="check" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
